import { Controller } from "@hotwired/stimulus";
import * as mapboxgl from 'mapbox-gl';
import { Map, MapboxEvent, Marker, LngLat, Popup } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { DrawSelectionChangeEvent } from '@mapbox/mapbox-gl-draw';
import * as StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import { LineString, Feature, Polygon } from 'geojson';
import * as $ from 'jquery';

import { translate } from "../../scripts/translate";
import * as TranslationsKeys from "../../scripts/locale/translationsKeys";

import { GpsCoordinates } from '../../scripts/sharedModels';
import { createMap, lngLatArrayToBounds, getDrawStyles, buildMapFeatureForTriggerZone, showTriggerZonePopup } from '../../scripts/mapbox';
import { postRequestAndWaitForJobCompletion, REQUEST_ID_HEADER, showErrorDialog } from '../../scripts/jobCompletion';
import { createOverlay, Overlay } from '../../scripts/loaderOverlay';
import { PointOfInterestMapMarkerModel } from '../../scripts/_generated/PointOfInterestMapMarkerModel';
import { GpsCoordinatesModel } from '../../scripts/_generated/GpsCoordinatesModel';
import { createPoiCreationMarker, getPoiMarkerPopupHtml } from '../Map/MapComponentShared';
import { reloadWithTurbo } from '../../scripts/turboHelper';
import { Colors } from '../../scripts/_generated/Colors';
import { formEditingCancelledEventName } from "../ViewOrEditForm/ViewOrEditFormController";
import { dialogConfirmationButtonClassName, dialogCancelButtonClassName } from "../../scripts/dialogConstants";
import { SaveTourMapArgs } from "../../scripts/_generated/SaveTourMapArgs";
import { EventHandlerRegistration, registerGlobalEventHandlerEventName } from "../../scripts/BodyController";
import { IBoundingBox } from "../../scripts/_generated/IBoundingBox";
import { DomEvents } from "../../scripts/_generated/DomEvents";

export const poisReorderedEventHandlerEventName = "tourPoisReordered";

// https://github.com/mapbox/mapbox-gl-draw/issues/667#issuecomment-418909342
const NoDragDirectSelect = Object.assign({},
    // import doesn't work :( import * as DirectSelect from '@mapbox/mapbox-gl-draw/src/modes/direct_select';
    // @ts-ignore
    MapboxDraw.modes.direct_select,
    {
        startDragging: function(state, e) {
            if (state.featureId === 'tour-route' && (!state.selectedCoordPaths || !state.selectedCoordPaths.length)) return;

            // copy pasted from node_modules\@mapbox\mapbox-gl-draw\src\modes\direct_select.js
            // @ts-ignore
            this.map.dragPan.disable();
            state.canDragMove = true;
            state.dragMoveLocation = e.lngLat;
        }
    });

export default class TourMapController extends Controller {
    static targets = [
        'container',
        'saveButton',
        'cancelButton',
        'deletePointsButton',
        'hint',
        'createTourPoi',
        'createRouteHintPoint',
        'generateRoute',
        'poiIndex'
    ];
    static values = {
        destinationUniqueId: String,
        tourUniqueId: String,
        tourLanguage: String,
        pois: Array,
        waypoints: Array,
        readonly: Boolean,
        getJobUrl: String,
        saveTourMapUrl: String,
        generateTourRouteUrl: String,
        boundingBox: Object,
        poiViewUrl: String,
        poiEditUrl: String,
        poiDetailUrl: String,
        isNewPoiDetailPageEnabled: Boolean,
        poiCreateUrl: String,
        routeHintCreateUrl: String,
        poiSetCoordinatesUrl: String,
    }

    readonly TOUR_ROUTE_FEATURE_ID = 'tour-route';

    readonly containerTarget!: HTMLElement;
    readonly saveButtonTarget!: HTMLButtonElement;
    readonly cancelButtonTarget!: HTMLButtonElement;
    readonly deletePointsButtonTarget!: HTMLButtonElement;
    readonly hintTarget!: HTMLElement;
    readonly hasCreateTourPoiTarget!: boolean;
    readonly createTourPoiTarget!: HTMLButtonElement;
    readonly createRouteHintPointTarget!: HTMLButtonElement;
    readonly generateRouteTarget!: HTMLButtonElement;
    readonly poiIndexTargets!: HTMLSpanElement[];

    readonly destinationUniqueIdValue!: string;
    readonly tourUniqueIdValue!: string;
    readonly tourLanguageValue!: string;
    readonly poisValue!: PointOfInterestMapMarkerModel[];
    waypointsValue!: GpsCoordinatesModel[];
    readonly readonlyValue!: boolean;
    readonly getJobUrlValue!: string;
    readonly saveTourMapUrlValue!: string;
    readonly generateTourRouteUrlValue!: string;
    readonly boundingBoxValue!: IBoundingBox;
    readonly poiEditUrlValue!: string;
    readonly poiViewUrlValue!: string;
    readonly poiDetailUrlValue!: string;
    readonly isNewPoiDetailPageEnabledValue!: boolean;
    readonly poiCreateUrlValue!: string;
    readonly routeHintCreateUrlValue!: string;
    readonly poiSetCoordinatesUrlValue!: string;

    map!: Map;
    draw!: MapboxDraw;
    overlay!: Overlay;
    createPoiMarker: Marker | undefined;
    createRouteHintMarker: Marker | undefined;
    poiMarkers: Marker[] = [];

    connect() {
        this.deletePointsButtonTarget.hidden = true;
        this.hintTarget.hidden = this.readonlyValue;

        this.containerTarget.onkeydown = e => {
            if (e.keyCode === 46 && this.draw.getSelectedPoints().features.length) {
                this.deletePoints();
            }
        };
        this.map = createMap(this.containerTarget);
        this.overlay = createOverlay(this.containerTarget);
        
        const drawModes = {
            ...MapboxDraw.modes, //TODO: single polyline only
            static: StaticMode,
            direct_select: NoDragDirectSelect,
        };

        this.draw = new MapboxDraw({
            defaultMode: this.readonlyValue ? 'static' : 'simple_select',
            userProperties: true,
            displayControlsDefault: false,
            // @ts-ignore
            modes: drawModes,
            // styling docs: https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/EXAMPLES.md
            styles: getDrawStyles()
        });

        this.map.addControl(this.draw);

        showTriggerZonePopup(this.map);

        this.map.on('load', (e: MapboxEvent) => {
            this.initializeMapFeatures();
        });

        this.map.on('draw.selectionchange', (e: DrawSelectionChangeEvent) => {
            // disable editing trigger zones
            // TODO: this could be better done using custom select mode https://stackoverflow.com/a/55143832/77160
            if (e.features.length && (e.features.length > 1 || e.features[0].id !== this.TOUR_ROUTE_FEATURE_ID)
                || this.createPoiMarker || this.createRouteHintMarker) {
                this.draw.changeMode('simple_select', { featureIds: [] });
                return;
            }

            if (e.features.length === 1 && e.features[0].id) {
                this.disableCreateTourPoi();
                this.disableCreateRouteHintPoint();

                this.draw.changeMode('direct_select', { featureId: `${e.features[0].id}` });
                this.hintTarget.innerHTML = `Now you can change the route by clicking and dragging the route points.
                <br/>If you need to remove a waypoint select it first (it turns red) than click the trash icon or use the <b>Delete</b> keyboard key. You can also select multiple waypoints to be deleted holding the <b>Shift</b> key.
                <br/>Save the changes once you're done using the <b>Save</b> button.`;
            }

            this.deletePointsButtonTarget.hidden = e.points.length === 0;

            if (this.draw.getMode() === 'simple_select') {
                this.setDefaultHintText();
                if (this.saveButtonTarget.disabled) {
                    this.enableCreateTourPoi();
                    this.enableCreateRouteHintPoint();
                }
            }
        });

        this.map.on('draw.update', (e: MapboxDraw.DrawUpdateEvent) => {
            //console.log(`draw.update`, e.features);
            this.setPendingChanges(true);
        });

        let sightCount = this.poisValue.filter(x => !x.isNavigation && !x.isRouteHint).length;
        if (sightCount < 2) {
            this.createRouteHintPointTarget.hidden = true;
        }

        this.registerTourPoisReorderedEventHandler();
    }

    registerTourPoisReorderedEventHandler() {
        this.dispatch(registerGlobalEventHandlerEventName, {
            prefix: "",
            detail: new EventHandlerRegistration<string[]>(poisReorderedEventHandlerEventName, this.handleTourPoisReorderedEvent.bind(this))
        });
    }

    handleTourPoisReorderedEvent(event: CustomEventInit<string[]>) {
        const orderedPoiUniqueIds = event.detail;
        if (!orderedPoiUniqueIds) throw new Error("Missing orderedPoiUniqueIds");
        this.poiIndexTargets.forEach(poiIndex => {
            const poiContentUniqueId = poiIndex.dataset.poiContentUniqueId;
            if (!poiContentUniqueId) throw new Error("Missing poiContentUniqueId");
            const newIndex = orderedPoiUniqueIds.indexOf(poiContentUniqueId);
            poiIndex.innerText = `${newIndex + 1}`;
        });
    }

    setDefaultHintText() {
        this.hintTarget.innerHTML = 'First add minimum of 2 Places using search bar above. Then auto-generate the route using the icon ' +
            '<img src="/img/highway.png" width="16" height="16" /> on the left. ' +
            'You can adjust the route by adding Routing Points <img src="/img/route_hint_point.png" width="16" height="16" />.';
    }

    initializeMapFeatures() {
        this.deletePointsButtonTarget.hidden = true;
        this.hintTarget.hidden = this.readonlyValue;

        this.setDefaultHintText();

        let poiNumber = 0;
        let routeHintNumber = 0;
        this.poisValue.forEach(poi => {
            poiNumber += poi.isNavigation || poi.isRouteHint ? 0 : 1;
            routeHintNumber += poi.isRouteHint ? 1 : 0;
            const el = document.createElement('div');
            let routeHintName = "";

            if (poi.isNavigation) {
                el.innerHTML =
                    `<span class="fa-stack fa-3x" title="Navigation point: ${poi.name}">
                <i class="fas fa-circle fa-stack-1x fa-inverse"></i>
                <i class="far fa-compass fa-stack-1x" style="color: ${Colors.defaultRouteColor}"></i>
                </span>`;
            }
            else if (poi.isRouteHint) {
                routeHintName = (routeHintNumber % 26 + 9).toString(36).toUpperCase();
                el.innerHTML =
                    `<span class="fa-stack fa-2x" title="Routing Point">
                <i class="fas fa-map-pin fa-stack-2x" style="color: ${Colors.smgPink}"></i>
                <span class="fa-stack-1x text-dark font-weight-bold h6" style="top: -10px">${routeHintName}</span>
                </span>`;
            }
            else {
                el.innerHTML =
                    `<span class="fa-stack fa-2x" title="${poi.name}">
                <i class="fas fa-map-marker fa-stack-2x" style="text-shadow: 0 0 3px #000; color: ${Colors.smgGreen}"></i>
                <span class="fa-stack-1x text-light"
                        style="top: -5px"
                        data-tour-map-target="poiIndex"
                        data-poi-content-unique-id="${poi.uniqueId}"
                >${poiNumber}</span>
                </span>`;
            }

            const isDraggable = poi.isRouteHint && !this.readonlyValue;

            const m = new Marker(el, {
                draggable: isDraggable,
                anchor: poi.isNavigation ? 'center' : 'bottom',
            });
            m.setLngLat(new mapboxgl.LngLat(poi.coordinates.longitude, poi.coordinates.latitude));

            // popup
            if (!poi.isRouteHint) {
                const editUrl = this.poiEditUrlValue
                    .replace('$poiUniqueId', poi.uniqueId)
                    .replace('$language', this.tourLanguageValue)
                    .replace('$destinationUniqueId', this.destinationUniqueIdValue)
                    .replace('$tourUniqueId', this.tourUniqueIdValue);
                const viewUrl = this.poiViewUrlValue
                    .replace('$poiUniqueId', poi.uniqueId)
                    .replace('$language', this.tourLanguageValue)
                    .replace('$destinationUniqueId', this.destinationUniqueIdValue)
                    .replace('$tourUniqueId', this.tourUniqueIdValue);
                const detailUrl = this.poiDetailUrlValue
                    .replace('$poiUniqueId', poi.uniqueId)
                    .replace('$language', this.tourLanguageValue)
                    .replace('$destinationUniqueId', this.destinationUniqueIdValue)
                    .replace('$tourUniqueId', this.tourUniqueIdValue);

                const poiDetailUrl = this.isNewPoiDetailPageEnabledValue
                    ? detailUrl
                    : (poi.isEditEnabled ? editUrl : (poi.isViewEnabled ? viewUrl : ''));

                m.setPopup(new Popup()
                    .setHTML(getPoiMarkerPopupHtml(poi, poiDetailUrl)));
            }

            // change location by dragging
            if (isDraggable) {
                m.on('dragend', () => {
                    const coordinates = m.getLngLat();

                    const url = this.poiSetCoordinatesUrlValue
                        .replace('$poiUniqueId', poi.uniqueId);

                    const formData = new FormData();
                    formData.append('latitude', `${coordinates.lat}`);
                    formData.append('longitude', `${coordinates.lng}`);

                    postRequestAndWaitForJobCompletion(url, formData, this.getJobUrlValue);
                });
            }

            m.addTo(this.map);
            this.poiMarkers.push(m);

            poi.triggerZones
                .map(tz => buildMapFeatureForTriggerZone(tz, true, `${(poi.isRouteHint ? `Route Hint ${routeHintName}` : `${poi.name}`)} trigger zone`))
                .forEach(this.draw.add);
        });

        let missingRouteBuilt = false;
        if (this.waypointsValue.length) {
            this.draw.add(this.buildRoute(this.waypointsValue));
        }
        else if (this.poisValue.length >= 2) {
            this.draw.add(this.buildRoute(this.poisValue.map(p => p.coordinates)));
            missingRouteBuilt = true;
        }

        const allCoordinates = this.waypointsValue.map(wp => new LngLat(wp.longitude, wp.latitude))
            .concat(this.poisValue.map(poi => new LngLat(poi.coordinates.longitude, poi.coordinates.latitude)));
        if (allCoordinates.length === 1) {
            this.map.setCenter(allCoordinates[0]);
            this.map.zoomTo(15);
        }
        else if (allCoordinates.length > 1) {
            const bounds = lngLatArrayToBounds(allCoordinates);
            this.map.fitBounds(bounds, { animate: false, padding: 100 });
        }
        else {
            const bounds = lngLatArrayToBounds([
                new LngLat(this.boundingBoxValue.northWest.longitude, this.boundingBoxValue.northWest.latitude),
                new LngLat(this.boundingBoxValue.southEast.longitude, this.boundingBoxValue.southEast.latitude)
            ]);
            this.map.fitBounds(bounds, { animate: false, padding: 100 });
        }
        this.setPendingChanges(missingRouteBuilt);
    }

    buildRoute(coordinates: GpsCoordinatesModel[]): Feature {
        return {
            id: this.TOUR_ROUTE_FEATURE_ID,
            type: 'Feature',
            properties: {
                color: Colors.defaultRouteColor,
            },
            geometry: {
                type: 'LineString',
                coordinates: coordinates.map(wp => [wp.longitude, wp.latitude])
            }
        }
    }

    setPendingChanges(pendingChanges: boolean) {
        this.saveButtonTarget.disabled = !pendingChanges;
        this.cancelButtonTarget.disabled = !pendingChanges;
    }

    save() {
        this.draw.changeMode('simple_select');
        const route = this.draw.get(this.TOUR_ROUTE_FEATURE_ID)?.geometry as LineString;
        if (!route || !route.coordinates || !route.coordinates.length) return;
        const routeWaypoints = route.coordinates.map(x => new GpsCoordinates(x[1], x[0]));
        const saveTourMapArgs: SaveTourMapArgs = {
            routeWaypoints: routeWaypoints
        };

        const url = this.saveTourMapUrlValue
            .replace("$uniqueId", this.tourUniqueIdValue)
            .replace("$language", this.tourLanguageValue);

        const formData = new FormData();
        formData.append("args", JSON.stringify(saveTourMapArgs));

        this.overlay.show();
        postRequestAndWaitForJobCompletion(url, formData, this.getJobUrlValue, undefined, () => {
            this.setPendingChanges(false);
            this.overlay.hide();
            this.refreshRouteWaypoints(route.coordinates.map(x => ({ latitude: x[1], longitude: x[0] })));
            this.deletePointsButtonTarget.hidden = true;

            this.enableCreateTourPoi();
            this.enableCreateRouteHintPoint();
            this.setDefaultHintText();

            this.dispatch(DomEvents.tourRouteGenerated);
        });
    }

    private refreshRouteWaypoints(coordinates: GpsCoordinatesModel[]) {
        this.waypointsValue = coordinates;
    }

    cancel() {
        this.draw.deleteAll();
        let m: Marker | undefined = undefined;
        while ((m = this.poiMarkers.pop())) {
            m.remove();
        }
        this.initializeMapFeatures();
        this.enableCreateTourPoi();
        this.enableCreateRouteHintPoint();
    }

    async generateRoute() {
        if (this.poisValue.length < 2) return;

        var _this = this;
        const hintTarget = this.hintTarget;

        bootbox.confirm({
            message: "Really generate a route around tour places?",
            buttons: {
                confirm: {
                    label: translate(TranslationsKeys.Global_Yes),
                    className: dialogConfirmationButtonClassName
                },
                cancel: {
                    label: translate(TranslationsKeys.Global_No),
                    className: dialogCancelButtonClassName
                }
            },
            async callback(result) {
                if (!result) return;

                _this.disableCreateTourPoi();
                _this.disableCreateRouteHintPoint();

                await _this.generateRouteInternal();
                hintTarget.innerHTML = `The tour route was automatically generated. You can save it using the <b>Save</b> button if you're happy with it or you can modify the tour route by clicking the violet line.`;
            }
        });        
    }

    async generateRouteInternal() {
        this.draw.changeMode('simple_select');
        this.overlay.show();

        const url = this.generateTourRouteUrlValue
            .replace("$uniqueId", this.tourUniqueIdValue)
            .replace("$language", this.tourLanguageValue);

        const response = await fetch(url);
        if (response.ok) {
            const data = (await response.json()) as GpsCoordinatesModel[];
            if (this.draw.get(this.TOUR_ROUTE_FEATURE_ID)) {
                this.draw.delete(this.TOUR_ROUTE_FEATURE_ID);
            }
            this.draw.add(this.buildRoute(data.map(c => ({ latitude: c.latitude, longitude: c.longitude }))));

            this.setPendingChanges(true);
        }
        this.overlay.hide();
        if (!response.ok) {
            const requestId = response.headers.get(REQUEST_ID_HEADER) || undefined;
            showErrorDialog('Failed to generate route', requestId);
        }
    }

    zoomIn() {
        this.map.zoomIn();
    }

    zoomOut() {
        this.map.zoomOut();
    }

    deletePoints() {
        this.draw.trash();
    }

    createTourPoi() {
        this.disableCreateTourPoi();
        this.disableCreateRouteHintPoint();
        this.disableGenerateRoute();

        if (this.createPoiMarker || this.createRouteHintMarker) return;

        const m = createPoiCreationMarker(this.map, coordinates =>
            this.poiCreateUrlValue
                .replace('$destinationUniqueId', this.destinationUniqueIdValue)
                .replace('$tourUniqueId', this.tourUniqueIdValue)
                .replace('$language', this.tourLanguageValue)
                .replace('$latitude', coordinates.lat.toString())
                .replace('$longitude', coordinates.lng.toString()),
            () => {
                this.enableCreateTourPoi();
                this.enableCreateRouteHintPoint();
                this.enableGenerateRoute();
                this.createPoiMarker = undefined;
                this.setDefaultHintText();
            }
        );

        this.createPoiMarker = m;
        this.hintTarget.innerHTML = "Move the Place marker on the map, and click <b>Create here</b>.";
    }

    disableCreateTourPoi() {
        if (this.hasCreateTourPoiTarget) {
            this.createTourPoiTarget.disabled = true;
        }
    }

    disableCreateRouteHintPoint() {
        this.createRouteHintPointTarget.disabled = true;
    }

    disableGenerateRoute() {
        this.generateRouteTarget.disabled = true;
    }

    enableCreateTourPoi() {
        if (this.hasCreateTourPoiTarget) {
            this.createTourPoiTarget.disabled = false;
        }
    }

    enableCreateRouteHintPoint() {
        this.createRouteHintPointTarget.disabled = false;
    }

    enableGenerateRoute() {
        this.generateRouteTarget.disabled = false;
    }

    createRouteHintPoint() {
        this.disableCreateTourPoi();
        this.disableCreateRouteHintPoint();
        this.disableGenerateRoute();

        if (this.createPoiMarker || this.createRouteHintMarker) return;

        const el = document.createElement('div');
        el.innerHTML =
            `<div class="text-center mb-0">
                 <div class="mb-1">
                    <a href="#" class="btn btn-sm btn-primary create-route-hint-point">Create here</a>
                    <a href="#" class="btn btn-sm btn-light cancel-create-route-hint-point"><i class="far fa-window-close"></i></a>
                 </div>
                 <div class="mb-0">
                    <i class="fas fa-map-pin fa-5x" style="text-shadow: 0 0 3px #000; color: ${Colors.smgPink}"></i>
                 </div>
             </div>
             `;


        const m = new Marker(el, {
            draggable: true,
            anchor: 'bottom'
        });
        m.setLngLat(this.map.getCenter());
        m.addTo(this.map);

        const createLink = el.getElementsByClassName("create-route-hint-point").item(0) as HTMLAnchorElement;
        if (createLink) {
            createLink.onclick = (e) => {
                e.preventDefault();
                this.overlay.show();
                const coordinates = m.getLngLat();
                const url = this.routeHintCreateUrlValue
                    .replace('$tourUniqueId', this.tourUniqueIdValue)
                    .replace('$language', this.tourLanguageValue);

                const formData = new FormData();
                formData.append('latitude', `${coordinates.lat}`);
                formData.append('longitude', `${coordinates.lng}`);

                postRequestAndWaitForJobCompletion(url, formData, this.getJobUrlValue, undefined, () => {
                    this.overlay.hide();
                    reloadWithTurbo();
                });
            };
        }
        const closeButton = el.getElementsByClassName("cancel-create-route-hint-point").item(0) as HTMLAnchorElement;
        closeButton.onclick = (e) => {
            e.preventDefault();
            m.remove();
            this.enableCreateTourPoi();
            this.enableCreateRouteHintPoint();
            this.enableGenerateRoute();
            this.createRouteHintMarker = undefined;
            this.setDefaultHintText();
        };

        this.createRouteHintMarker = m;
        this.hintTarget.innerHTML = "Move the routing point marker on the map, and click <b>Create here</b>.";
    }
}
